import { IconButton } from "@mui/material";
import React, { useRef, useState } from "react";
import { getDir } from "orion-components/i18n/Config/selector";
import { useSelector } from "react-redux";
import { cameraService } from "client-app-core";
import $ from "jquery";

let initialY = null;
let initialX = null;

const analogSpeedCalc = (start, end) => {
	const pieces = 5;
	const step = (end - start) / (pieces - 1);
	const array = [];

	for (let i = 0; i < pieces; i++) {
		array.push(start + i * step);
	}
	return array;
};

function getSpeedRange(value, arr, tolerance = 1e-5) {
	if (value > arr[arr.length - 1]) {
		return arr.length * 2;
	}

	let nearestIndex = -1;
	let smallestDifference = Infinity;

	for (let i = 0; i < arr.length; i++) {
		const difference = Math.abs(arr[i] - value);
		if (difference < tolerance) {
			return i; // Found exact match
		}
		if (difference < smallestDifference) {
			smallestDifference = difference;
			nearestIndex = i;
		}
	}
	return nearestIndex * 2;
}

const AnalogButton = ({ camera, instanceId, getDraggingState }) => {
	const buttonRef = useRef(null);
	const parentRef = useRef(null);
	const [dragging, setDragging] = useState(false);
	const dir = useSelector((state) => getDir(state));

	const stopContinuousPanTilt = () => {
		cameraService.moveContinuous(camera.id, instanceId, 0, 0, 0);
		cameraService.moveContinuous(camera.id, instanceId, 0, 0, 0);
	};

	const handlePointerDown = (e) => {
		// prevent page scroll on touch and keep events on this element
		e.preventDefault();
		e.currentTarget.setPointerCapture(e.pointerId);

		initialY = e.clientY;
		initialX = e.clientX;
		buttonRef.current.lastX = 0;
		buttonRef.current.lastY = 0;

		setDragging(true);
		getDraggingState?.(true);
	};

	const handlePointerUpOrCancel = (e) => {
		if (buttonRef.current?.hasPointerCapture?.(e.pointerId)) {
			buttonRef.current.releasePointerCapture(e.pointerId);
		}
		if (dragging) {
			setDragging(false);
			getDraggingState?.(false);
			stopContinuousPanTilt();
		}
		// reset knob position
		if (dir === "ltr") {
			buttonRef.current.style.left = "";
		} else {
			buttonRef.current.style.right = "";
		}
		buttonRef.current.style.top = "30%";
		e.stopPropagation();
	};

	const panSpeedRangeCalculator = (value) => {
		const parentRect = parentRef.current.getBoundingClientRect();
		const childRect = buttonRef.current.getBoundingClientRect();
		const range = (parentRect.width - childRect.width) / 2;
		const speedRange = analogSpeedCalc(0, range);
		return getSpeedRange(Math.abs(value), speedRange);
	};

	const tiltSpeedRangeCalculator = (value) => {
		const parentRect = parentRef.current.getBoundingClientRect();
		const childRect = buttonRef.current.getBoundingClientRect();
		const range = (parentRect.height - childRect.height) / 2;
		const speedRange = analogSpeedCalc(0, range);
		return getSpeedRange(Math.abs(value), speedRange);
	};

	const handlePointerMove = (event) => {
		if (!dragging) return;

		// --- visual knob movement inside container ---
		const rect = buttonRef.current.getBoundingClientRect();
		const containerRect = buttonRef.current.parentElement.getBoundingClientRect();

		if (dir === "ltr") {
			const newX = event.clientX - containerRect.left - rect.width / 2;
			const newY = event.clientY - containerRect.top - rect.height / 2;

			if (Math.max(0, Math.min(containerRect.width - rect.width, newX)) < 37) {
				buttonRef.current.style.left = `${Math.max(0, Math.min(containerRect.width - rect.width, newX))}px`;
			}
			buttonRef.current.style.top = `${Math.max(0, Math.min(containerRect.height - rect.height, newY))}px`;
		} else {
			const newX = event.clientX - containerRect.right - rect.width / 2;
			const newY = event.clientY - containerRect.top - rect.height / 2;

			if (Math.max(0, Math.min(containerRect.width - rect.width, newX)) < 37) {
				buttonRef.current.style.right = `${Math.max(0, Math.min(containerRect.width - rect.width, newX))}px`;
			}
			buttonRef.current.style.top = `${Math.max(0, Math.min(containerRect.height - rect.height, newY))}px`;
		}

		// --- PTZ math ---
		if (event?.clientX || event?.clientY) {
			const x = initialX - event.clientX;
			const y = initialY - event.clientY;

			let panFactor = camera.entityData.properties.analogPanFactor || 10;
			let tiltFactor = camera.entityData.properties.analogTiltFactor || 10;

			const panDir = x < 0 ? 1 : -1 * (panFactor < 0 ? -1 : 1);
			const tiltDir = y > 0 ? 1 : -1 * (tiltFactor < 0 ? -1 : 1);

			panFactor = Math.abs(panFactor);
			tiltFactor = Math.abs(tiltFactor);

			let panSpd = 0;
			if (buttonRef.current) panSpd = panSpeedRangeCalculator(x);
			panSpd = Math.min(1, panSpd / panFactor);

			let tiltSpd = 0;
			if (buttonRef.current) tiltSpd = tiltSpeedRangeCalculator(y);
			tiltSpd = Math.min(1, tiltSpd / tiltFactor);

			if (buttonRef.current.lastX !== panSpd * panDir) {
				if (!(panDir !== 0 && panSpd === 0)) {
					cameraService.moveContinuous(camera.id, instanceId, panSpd * panDir, 0, 0);
					buttonRef.current.lastX = panSpd * panDir;
				}
			}

			if (buttonRef.current.lastY !== tiltSpd * tiltDir) {
				if (!(tiltDir !== 0 && tiltSpd === 0)) {
					cameraService.moveContinuous(camera.id, instanceId, 0, tiltSpd * tiltDir, 0);
					buttonRef.current.lastY = tiltSpd * tiltDir;
				}
			}
		}
	};

	const _onMouseWheel = (e) => {
		let zoomFactor = camera.entityData.properties.analogZoomFactor || 20;
		const zoomDirFactor = zoomFactor < 0 ? 1 : -1;
		zoomFactor = Math.abs(zoomFactor);
		const distance = Math.abs(Math.round(e.nativeEvent.deltaY / 100) / zoomFactor);
		if (distance !== 0) {
			const direction = (e.nativeEvent.deltaY < 0 ? -1 : 1) * zoomDirFactor;
			relativeZoom(direction, distance, 1);
		}
	};

	const relativeZoom = (direction, distance, speed = 1) => {
		cameraService.zoomRelative(camera.id, instanceId, direction, distance, speed);
	};

	return (
		<div style={{ width: "100%", height: "100%" }} ref={parentRef}>
			<div
				ref={buttonRef}
				style={{
					position: "absolute",
					...(dir === "ltr" && { right: "28.5%" }),
					...(dir === "rtl" && { left: "28.5%" }),
					top: "30%",
					display: "flex"
				}}
				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={handlePointerUpOrCancel}
				onPointerCancel={handlePointerUpOrCancel}
				onWheel={_onMouseWheel} data-testid="orion-components-cbcomponents-new-video-player-components-ptz-analog-button-div"
			>
				<IconButton
					sx={{
						"&.MuiIconButton-root": {
							padding: "0px !important"
						}
					}} data-testid="orion-components-cbcomponents-new-video-player-components-ptz-analog-button-icon-button"
				>
					<div
						style={{
							position: "relative",
							width: 22.5,
							height: 22.5,
							background: "#BDBBBB",
							borderRadius: "50%",
							border: "3px solid #888787"
						}}
					/>
				</IconButton>
			</div>
		</div>
	);
};

export default AnalogButton;
